import { create } from "zustand";
import {
  type ExpenseCategory,
  type ExpenseItem,
  type InvestmentProperty,
  type PersistedState,
  type Scenario,
  defaultPersistedState,
  migrateState
} from "@aus-property/core";
import { AppRepository } from "@/persistence/repository";
import { demoPortfolio, createPropertyTemplate } from "@/data/demoPortfolio";
import type { StatementImportResult } from "@/features/integrations/types";
import { createId } from "@/utils/id";
import {
  getAuthSecurityState,
  getCurrentIdentity,
  resendSignUpEmail,
  signInWithEmail,
  signOutCloud,
  signUpWithEmail,
  type CloudIdentity
} from "@/integrations/cloud/authService";
import {
  pullPortfolioBackup,
  pushPortfolioBackup
} from "@/integrations/cloud/syncService";
import { isCloudConfigured } from "@/integrations/cloud/cloudConfig";
import { fetchEntitlementTier } from "@/integrations/cloud/entitlementService";
import {
  isEntitlementApiConfigured,
  restoreStorePurchases as restoreStorePurchasesRemote,
  verifyStorePurchase
} from "@/integrations/billing/storeEntitlements";
import {
  restoreNativePurchases,
  syncLatestNativePurchase
} from "@/integrations/billing/nativeStoreBridge";

export type TabKey =
  | "dashboard"
  | "properties"
  | "scenarios"
  | "insights"
  | "settings";

interface AppStore {
  state: PersistedState;
  activeTab: TabKey;
  selectedPropertyId: string | null;
  initialized: boolean;
  busy: boolean;
  errorMessage: string | null;
  cloudConfigured: boolean;
  authIdentity: CloudIdentity | null;
  authAssuranceLevel: "aal1" | "aal2" | "unknown";
  authBusy: boolean;
  cloudBusy: boolean;
  cloudStatusMessage: string | null;
  isPro: boolean;
  proStatusMessage: string | null;
  initialize: () => Promise<void>;
  refreshCloudAuth: () => Promise<void>;
  refreshEntitlements: () => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  resendVerificationEmail: (email: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  syncToCloud: () => Promise<void>;
  restoreFromCloud: () => Promise<void>;
  syncLatestStorePurchase: () => Promise<void>;
  restoreStorePurchases: () => Promise<void>;
  setActiveTab: (tab: TabKey) => void;
  setTheme: (theme: "light" | "dark") => Promise<void>;
  markOnboardingComplete: () => Promise<void>;
  loadDemo: () => Promise<void>;
  addProperty: (property: InvestmentProperty) => Promise<void>;
  updateProperty: (property: InvestmentProperty) => Promise<void>;
  removeProperty: (propertyId: string) => Promise<void>;
  selectProperty: (propertyId: string | null) => void;
  updateAssumptions: (years: number) => Promise<void>;
  addScenario: (scenario: Scenario) => Promise<void>;
  importState: (raw: unknown) => Promise<void>;
  applyStatementImport: (
    propertyId: string,
    statementResult: StatementImportResult
  ) => Promise<void>;
}

const repository = new AppRepository();

const withUpdatedTimestamp = (state: PersistedState): PersistedState => ({
  ...state,
  portfolio: {
    ...state.portfolio,
    updatedAt: new Date().toISOString()
  }
});

const persistState = async (state: PersistedState): Promise<void> => {
  await repository.save(withUpdatedTimestamp(state));
};

const LOCAL_ONLY_SAVE_MESSAGE =
  "Saved on this device. Sign in to back up and use your portfolio on another device.";

const deriveIsPro = (state: PersistedState): boolean =>
  state.settings.planTier === "PRO" || Boolean(state.settings.proEnabled);

const coerceFreeTier = (state: PersistedState): PersistedState => ({
  ...state,
  settings: {
    ...state.settings,
    planTier: "FREE",
    proEnabled: false
  }
});

const FREE_PROPERTY_LIMIT = 1;

const toAuthErrorMessage = (error: unknown): string => {
  const message = error instanceof Error ? error.message : "Unexpected error";
  const status = (error as { status?: unknown } | null)?.status;

  if (status === 429) {
    return "Too many attempts. Please wait a few minutes and try again.";
  }

  if (/rate limit/i.test(message)) {
    return "Too many attempts. Please wait a few minutes and try again.";
  }

  return message;
};

const withSaveReminder = (
  currentStore: AppStore,
  overrides: Partial<AppStore>
): Partial<AppStore> => {
  if (!currentStore.cloudConfigured || currentStore.authIdentity) {
    return overrides;
  }

  return {
    ...overrides,
    cloudStatusMessage: LOCAL_ONLY_SAVE_MESSAGE
  };
};

const mapStatementCategory = (input: string): string => {
  const value = input.toLowerCase();

  if (value.includes("agent")) return "Agent Fees";
  if (value.includes("repair")) return "Repairs & Maintenance";
  if (value.includes("insur")) return "Insurance";
  if (value.includes("strata")) return "Strata";
  if (value.includes("water")) return "Water";
  if (value.includes("rate")) return "Rates";
  if (value.includes("rent")) return "Income/Rent";

  return "Other";
};

const toForecastYear = (
  purchaseDateIso: string,
  period: string
): number | null => {
  const purchaseYear = new Date(purchaseDateIso).getFullYear();
  const periodYear = Number(period.slice(0, 4));
  if (!Number.isFinite(periodYear)) return null;
  const relativeYear = periodYear - purchaseYear + 1;
  return relativeYear >= 1 ? relativeYear : null;
};

const applyStatementToProperty = (
  property: InvestmentProperty,
  statementResult: StatementImportResult
): InvestmentProperty => {
  const categories: ExpenseCategory[] = property.expenses.categories.map(
    (category) => ({
      ...category,
      items: category.items.map((item) => ({ ...item }))
    })
  );

  let importedRentAdjustment = 0;

  for (const row of statementResult.totalsByPeriodCategory) {
    const mappedCategory = mapStatementCategory(row.category);

    if (mappedCategory === "Income/Rent") {
      importedRentAdjustment += row.amount;
      continue;
    }

    const forecastYear = toForecastYear(
      property.purchase.purchaseDate,
      row.period
    );
    if (!forecastYear) {
      continue;
    }

    const targetCategory =
      categories.find((category) => category.name === mappedCategory) ??
      (() => {
        const created: ExpenseCategory = {
          id: createId("cat"),
          name: mappedCategory,
          items: []
        };
        categories.push(created);
        return created;
      })();

    const amount = Math.abs(row.amount);
    const importedItem: ExpenseItem = {
      id: createId("stmt"),
      name: `Statement import ${row.period}`,
      amount,
      cadence: "ONE_OFF",
      appliesInYears: [forecastYear],
      growthRate: 0,
      notes: "Imported from PDF statement"
    };

    targetCategory.items.push(importedItem);
  }

  return {
    ...property,
    income: {
      ...property.income,
      otherAnnualIncome:
        property.income.otherAnnualIncome + Math.max(importedRentAdjustment, 0)
    },
    expenses: {
      ...property.expenses,
      categories
    },
    updatedAt: new Date().toISOString()
  };
};

export const useAppStore = create<AppStore>((set, get) => ({
  state: defaultPersistedState(),
  activeTab: "dashboard",
  selectedPropertyId: null,
  initialized: false,
  busy: false,
  errorMessage: null,
  cloudConfigured: isCloudConfigured,
  authIdentity: null,
  authAssuranceLevel: "unknown",
  authBusy: false,
  cloudBusy: false,
  cloudStatusMessage: null,
  isPro: deriveIsPro(defaultPersistedState()),
  proStatusMessage: null,

  initialize: async () => {
    set({ busy: true, errorMessage: null });
    try {
      const persisted = await repository.load();
      const safeState = coerceFreeTier(persisted);
      set({
        state: safeState,
        initialized: true,
        busy: false,
        selectedPropertyId: safeState.portfolio.properties[0]?.id ?? null,
        isPro: deriveIsPro(safeState)
      });

      if (isCloudConfigured) {
        await get().refreshCloudAuth();
        await get().refreshEntitlements();
      }
    } catch (error) {
      set({
        busy: false,
        initialized: true,
        errorMessage:
          error instanceof Error ? error.message : "Unable to initialize app"
      });
    }
  },

  refreshCloudAuth: async () => {
    if (!isCloudConfigured) {
      set({
        authIdentity: null,
        authAssuranceLevel: "unknown"
      });
      return;
    }

    try {
      const [identity, security] = await Promise.all([
        getCurrentIdentity(),
        getAuthSecurityState()
      ]);

      if (!identity) {
        const current = get().state;
        const next = coerceFreeTier(current);
        set({
          authIdentity: null,
          authAssuranceLevel: security.aal,
          state: next,
          isPro: false
        });
        await persistState(next);
        return;
      }

      set({
        authIdentity: identity,
        authAssuranceLevel: security.aal
      });
    } catch (error) {
      set({
        cloudStatusMessage:
          error instanceof Error
            ? error.message
            : "Unable to refresh cloud auth"
      });
    }
  },

  refreshEntitlements: async () => {
    const identity = get().authIdentity;
    if (!identity) {
      const current = get().state;
      const next = coerceFreeTier(current);
      set({
        state: next,
        isPro: false
      });
      await persistState(next);
      return;
    }

    try {
      const tier = await fetchEntitlementTier(identity);
      const current = get().state;
      const next: PersistedState = {
        ...current,
        settings: {
          ...current.settings,
          planTier: tier,
          proEnabled: tier === "PRO"
        }
      };

      set({
        state: next,
        isPro: deriveIsPro(next)
      });
      await persistState(next);
    } catch (error) {
      set({
        proStatusMessage:
          error instanceof Error
            ? error.message
            : "Unable to refresh entitlement status"
      });
    }
  },

  signUp: async (email, password) => {
    set({ authBusy: true, cloudStatusMessage: null });
    try {
      await signUpWithEmail(email, password);
      set({
        authBusy: false,
        cloudStatusMessage:
          "Account created. Check your email and verify your address before signing in."
      });
    } catch (error) {
      set({
        authBusy: false,
        cloudStatusMessage:
          toAuthErrorMessage(error) || "Sign up failed"
      });
    }
  },

  resendVerificationEmail: async (email) => {
    const value = email.trim();
    if (!value) {
      set({ cloudStatusMessage: "Enter your email above, then resend." });
      return;
    }

    set({ authBusy: true, cloudStatusMessage: null });
    try {
      await resendSignUpEmail(value);
      set({
        authBusy: false,
        cloudStatusMessage: "Verification email sent. Check your inbox and spam folder."
      });
    } catch (error) {
      set({
        authBusy: false,
        cloudStatusMessage: toAuthErrorMessage(error) || "Unable to resend email."
      });
    }
  },

  signIn: async (email, password) => {
    set({ authBusy: true, cloudStatusMessage: null });
    try {
      const identity = await signInWithEmail(email, password);
      set({
        authBusy: false,
        authIdentity: identity,
        cloudStatusMessage: `Signed in as ${identity.email ?? identity.id}`
      });
      await get().refreshCloudAuth();
      await get().refreshEntitlements();
    } catch (error) {
      set({
        authBusy: false,
        cloudStatusMessage:
          toAuthErrorMessage(error) || "Sign in failed"
      });
    }
  },

  signOut: async () => {
    set({ authBusy: true, cloudStatusMessage: null });
    try {
      await signOutCloud();
      const current = get().state;
      const next = coerceFreeTier(current);
      set({
        authBusy: false,
        authIdentity: null,
        authAssuranceLevel: "unknown",
        cloudStatusMessage: "Signed out",
        state: next,
        isPro: false
      });
      await persistState(next);
    } catch (error) {
      set({
        authBusy: false,
        cloudStatusMessage:
          error instanceof Error ? error.message : "Sign out failed"
      });
    }
  },

  syncToCloud: async () => {
    const identity = get().authIdentity;
    if (!identity) {
      set({ cloudStatusMessage: "Sign in to back up your portfolio." });
      return;
    }

    set({ cloudBusy: true, cloudStatusMessage: null });
    try {
      await pushPortfolioBackup(identity, get().state);
      set({ cloudBusy: false, cloudStatusMessage: "Backup saved." });
    } catch (error) {
      set({
        cloudBusy: false,
        cloudStatusMessage:
          error instanceof Error ? error.message : "Backup failed"
      });
    }
  },

  restoreFromCloud: async () => {
    const identity = get().authIdentity;
    if (!identity) {
      set({ cloudStatusMessage: "Sign in to restore a backup." });
      return;
    }

    set({ cloudBusy: true, cloudStatusMessage: null });
    try {
      const payload = await pullPortfolioBackup(identity);
      if (!payload) {
        set({
          cloudBusy: false,
          cloudStatusMessage: "No backup found yet."
        });
        return;
      }

      const migrated = migrateState(payload);
      set({
        state: migrated,
        selectedPropertyId: migrated.portfolio.properties[0]?.id ?? null,
        cloudBusy: false,
        cloudStatusMessage: "Backup restored.",
        isPro: deriveIsPro(migrated)
      });
      await persistState(migrated);
      await get().refreshEntitlements();
    } catch (error) {
      set({
        cloudBusy: false,
        cloudStatusMessage:
          error instanceof Error ? error.message : "Restore failed"
      });
    }
  },

  syncLatestStorePurchase: async () => {
    if (!isEntitlementApiConfigured) {
      set({
        proStatusMessage:
          "Cloud backend is not configured. Set VITE_CLOUD_API_URL."
      });
      return;
    }

    const identity = get().authIdentity;
    if (!identity) {
      set({
        proStatusMessage: "Sign in before syncing your subscription."
      });
      return;
    }

    set({ cloudBusy: true, proStatusMessage: null });
    try {
      const purchase = await syncLatestNativePurchase();
      await verifyStorePurchase(purchase);
      await get().refreshEntitlements();
      set({
        cloudBusy: false,
        proStatusMessage:
          "Subscription synced. Your account now has the latest store entitlement."
      });
    } catch (error) {
      set({
        cloudBusy: false,
        proStatusMessage:
          error instanceof Error
            ? error.message
            : "Unable to sync subscription purchase."
      });
    }
  },

  restoreStorePurchases: async () => {
    if (!isEntitlementApiConfigured) {
      set({
        proStatusMessage:
          "Cloud backend is not configured. Set VITE_CLOUD_API_URL."
      });
      return;
    }

    const identity = get().authIdentity;
    if (!identity) {
      set({
        proStatusMessage: "Sign in before restoring purchases."
      });
      return;
    }

    set({ cloudBusy: true, proStatusMessage: null });
    try {
      const purchases = await restoreNativePurchases();
      if (purchases.length === 0) {
        set({
          cloudBusy: false,
          proStatusMessage: "No restorable subscriptions were found for this store account."
        });
        return;
      }

      const result = await restoreStorePurchasesRemote(purchases);
      await get().refreshEntitlements();

      const issueSummary =
        result.issues.length > 0
          ? ` Some items need attention (${result.issues.length}).`
          : "";

      set({
        cloudBusy: false,
        proStatusMessage: `Restore completed for ${result.verifiedCount} purchase(s).${issueSummary}`
      });
    } catch (error) {
      set({
        cloudBusy: false,
        proStatusMessage:
          error instanceof Error ? error.message : "Unable to restore purchases."
      });
    }
  },

  setActiveTab: (tab) => set({ activeTab: tab }),

  setTheme: async (theme) => {
    const current = get().state;
    const next = { ...current, settings: { ...current.settings, theme } };
    set({ state: next });
    await persistState(next);
  },

  markOnboardingComplete: async () => {
    const current = get().state;
    const next = {
      ...current,
      settings: {
        ...current.settings,
        onboardingCompleted: true
      }
    };
    set({ state: next });
    await persistState(next);
  },

  loadDemo: async () => {
    const current = get().state;
    const next: PersistedState = {
      ...current,
      portfolio: demoPortfolio(),
      settings: {
        ...current.settings,
        onboardingCompleted: true
      }
    };
    set({
      state: next,
      selectedPropertyId: next.portfolio.properties[0]?.id ?? null,
      isPro: deriveIsPro(next)
    });
    await persistState(next);
  },

  addProperty: async (property) => {
    const currentStore = get();
    const current = currentStore.state;

    if (!currentStore.isPro && current.portfolio.properties.length >= FREE_PROPERTY_LIMIT) {
      set({
        proStatusMessage:
          "Free includes 1 property. Start a subscription on mobile and sync purchase to unlock unlimited properties.",
        activeTab: "settings"
      });
      return;
    }

    const next = {
      ...current,
      portfolio: {
        ...current.portfolio,
        properties: [...current.portfolio.properties, property]
      }
    };

    set(
      withSaveReminder(currentStore, {
        state: next,
        selectedPropertyId: property.id,
        isPro: deriveIsPro(next)
      })
    );
    await persistState(next);
  },

  updateProperty: async (property) => {
    const currentStore = get();
    const current = currentStore.state;

    if (!currentStore.isPro && current.portfolio.properties.length > FREE_PROPERTY_LIMIT) {
      set({
        proStatusMessage:
          "Subscribe on mobile to edit portfolios with more than 1 property.",
        activeTab: "settings"
      });
      return;
    }

    const next = {
      ...current,
      portfolio: {
        ...current.portfolio,
        properties: current.portfolio.properties.map((existing) =>
          existing.id === property.id ? property : existing
        )
      }
    };

    set(
      withSaveReminder(currentStore, {
        state: next,
        isPro: deriveIsPro(next)
      })
    );
    await persistState(next);
  },

  removeProperty: async (propertyId) => {
    const currentStore = get();
    const current = currentStore.state;

    const remaining = current.portfolio.properties.filter(
      (property) => property.id !== propertyId
    );

    const next = {
      ...current,
      portfolio: {
        ...current.portfolio,
        properties: remaining
      }
    };

    set(
      withSaveReminder(currentStore, {
        state: next,
        selectedPropertyId: remaining[0]?.id ?? null,
        isPro: deriveIsPro(next)
      })
    );
    await persistState(next);
  },

  selectProperty: (propertyId) => set({ selectedPropertyId: propertyId }),

  updateAssumptions: async (years) => {
    const currentStore = get();
    const current = currentStore.state;
    const next = {
      ...current,
      portfolio: {
        ...current.portfolio,
        assumptions: {
          ...current.portfolio.assumptions,
          forecastYears: years
        }
      }
    };

    set(
      withSaveReminder(currentStore, {
        state: next,
        isPro: deriveIsPro(next)
      })
    );
    await persistState(next);
  },

  addScenario: async (scenario) => {
    const currentStore = get();
    const current = currentStore.state;

    const exists = current.scenarios.find(
      (existing) => existing.id === scenario.id
    );

    const next = {
      ...current,
      scenarios: exists
        ? current.scenarios.map((existing) =>
            existing.id === scenario.id ? scenario : existing
          )
        : [...current.scenarios, scenario]
    };

    set(
      withSaveReminder(currentStore, {
        state: next
      })
    );
    await persistState(next);
  },

  importState: async (raw) => {
    const migrated = migrateState(raw);
    const currentStore = get();
    set(
      withSaveReminder(currentStore, {
        state: migrated,
        selectedPropertyId: migrated.portfolio.properties[0]?.id ?? null,
        errorMessage: null,
        isPro: deriveIsPro(migrated)
      })
    );
    await persistState(migrated);
  },

  applyStatementImport: async (propertyId, statementResult) => {
    const currentStore = get();
    if (!currentStore.isPro) {
      set({
        proStatusMessage:
          "PDF statement import is available on paid subscriptions. Start or restore your subscription on mobile, then sync purchases.",
        activeTab: "settings"
      });
      return;
    }

    const current = currentStore.state;
    const nextProperties = current.portfolio.properties.map((property) =>
      property.id === propertyId
        ? applyStatementToProperty(property, statementResult)
        : property
    );

    const next = {
      ...current,
      portfolio: {
        ...current.portfolio,
        properties: nextProperties
      }
    };

    set(
      withSaveReminder(currentStore, {
        state: next,
        isPro: deriveIsPro(next)
      })
    );
    await persistState(next);
  }
}));

export const propertyTemplate = createPropertyTemplate;
